'use client'

import { NavigationMenu as NavigationMenuPrimitive } from 'radix-ui'
import React from 'react'
import { cn } from '../../utils'

/**
 * Desktop navigation bar. Wraps Radix `NavigationMenu.Root` and renders its own
 * viewport, so compose it with `WideNavigationMenuList` only — every Radix
 * `NavigationMenu.Root` prop (`value`, `defaultValue`, `delayDuration`,
 * `orientation`, `dir`) is forwarded.
 */
const WideNavigationMenu = ({
  className,
  children,
  ref,
  ...props
}: React.ComponentProps<typeof NavigationMenuPrimitive.Root>) => (
  <NavigationMenuPrimitive.Root
    ref={ref}
    className={cn(
      'relative z-10 flex h-[34px] w-full flex-1 items-center border-b-[1px] border-gray-300 bg-white 2xl:h-[40px] [&>div]:first:h-full [&>div]:first:w-full',
      className,
    )}
    {...props}
  >
    {children}

    <WideNavigationMenuViewport />
  </NavigationMenuPrimitive.Root>
)

WideNavigationMenu.displayName = NavigationMenuPrimitive.Root.displayName

const WideNavigationMenuList = ({
  className,
  ref,
  ...props
}: React.ComponentProps<typeof NavigationMenuPrimitive.List>) => (
  <NavigationMenuPrimitive.List
    ref={ref}
    className={cn(
      'group container box-border flex h-full w-full flex-1 list-none items-center justify-between bg-white',
      'text-gray-600 [&:has([data-state="open"])]:text-gray-400', // changes closed links color when menu is open
      className,
    )}
    {...props}
  />
)

WideNavigationMenuList.displayName = NavigationMenuPrimitive.List.displayName

const WideNavigationMenuItem = ({
  ...props
}: React.ComponentProps<typeof NavigationMenuPrimitive.Item>) => (
  <NavigationMenuPrimitive.Item {...props} />
)

WideNavigationMenuItem.displayName = NavigationMenuPrimitive.Root.displayName

const WideNavigationMenuTrigger = <T extends React.ElementType = 'a'>({
  children,
  ref,
  ...props
}: React.ComponentProps<typeof NavigationMenuPrimitive.Trigger> &
  React.ComponentPropsWithoutRef<T> & { as?: T }) => (
  <NavigationMenuPrimitive.Trigger ref={ref} asChild {...props}>
    <WideNavigationMenuLink>{children}</WideNavigationMenuLink>
  </NavigationMenuPrimitive.Trigger>
)

WideNavigationMenuTrigger.displayName =
  NavigationMenuPrimitive.Trigger.displayName

const WideNavigationMenuLink = <T extends React.ElementType = 'a'>({
  className,
  children,
  as,
  ...props
}: React.ComponentPropsWithoutRef<T> & { as?: T }) => {
  const Component = as || 'a'

  return (
    <Component
      className={cn(
        'group/trigger group relative inline-flex h-9 w-max items-center justify-center text-xs font-normal uppercase disabled:pointer-events-none disabled:opacity-50',
        'transition-colors duration-300 data-[state=open]:text-gray-600',
        className,
      )}
      {...props}
    >
      <>
        {/* hidden label for avoid layout shift when user hovers the text */}
        <span className="invisible font-bold" aria-hidden="true">
          {children}
        </span>

        <span
          className={cn(
            'absolute group-hover/trigger:font-bold group-hover/trigger:underline',
            'group-data-[state=open]:font-bold',
          )}
        >
          {children}
        </span>
      </>
    </Component>
  )
}

WideNavigationMenuLink.displayName = 'WideNavigationMenuLink'

const WideNavigationMenuViewport = ({
  className,
  ref,
  children,
  ...props
}: React.ComponentProps<typeof NavigationMenuPrimitive.Viewport>) => (
  <div className={cn('absolute left-0 top-full -z-10 mt-[1px] w-full')}>
    <NavigationMenuPrimitive.Viewport
      className={cn(
        'data-[state=closed]:animate-nav-menu-close data-[state=open]:animate-nav-menu-open transition-height peer relative h-[var(--radix-navigation-menu-viewport-height)] w-full overflow-hidden bg-white duration-300',
        className,
      )}
      ref={ref}
      {...props}
      style={{
        interpolateSize: 'allow-keywords',
      }}
    >
      {children}
    </NavigationMenuPrimitive.Viewport>

    <WideNavigationMenuOverlay className="peer-empty:!invisible peer-data-[state]:visible peer-data-[state=open]:opacity-70" />
  </div>
)

WideNavigationMenuViewport.displayName =
  NavigationMenuPrimitive.Viewport.displayName

const WideNavigationMenuOverlay = ({
  className,
  ...props
}: React.ComponentProps<'div'>) => (
  <div
    className={cn(
      'invisible absolute left-0 top-0 -z-10 h-screen w-full bg-black opacity-0 transition-opacity duration-300',
      className,
    )}
    aria-hidden="true"
    {...props}
  />
)

export {
  WideNavigationMenu,
  WideNavigationMenuItem,
  WideNavigationMenuLink,
  WideNavigationMenuList,
  WideNavigationMenuTrigger,
}
